import React from "react"
import { RowsContainer } from "./FlexDiv"
import { useLocation } from "react-router-dom"
import styled from "styled-components"
import MenuWindow, { type Item } from "./MenuWindow"
import DropdownButton from "./DropdownButton"
import Text from "./Text"
import { post } from "DealerApp/helpers/InputFormAndSubmit"
import type { User } from "DealerApp/contexts/userContext"
import type Texts from "DealerApp/types/texts"

const DropdownButtonLabel = styled("div")``

const HeaderItemsWrapper = styled(RowsContainer)`
  display: inline-block;
  box-shadow: ${({ theme }) => theme.gray_300} 0px 10px 10px -10px;
  padding: 20px 0px;
`

const ProductName = styled.div`
  font-size: 20px;
  color: ${({ theme }) => theme.blue_800};
  font-weight: 500;
  float: left;
  padding-left: 24px;
  margin: auto;
  padding: 10px 0px 10px 24px;
`

const CustomerName = styled.div`
  font-size: 14px;
  color: ${({ theme }) => theme.blue_800};
  font-weight: 550;
  margin: auto;
  padding: 10px;
`
const DropdownsWrapper = styled.div`
  float: right;
  display: flex;
`

const DropdownSelection = styled.div`
  margin-right: 24px;
`

const UserLabel = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background-color: ${({ theme }) => theme.orange_300};
`

export type HeaderMenuItem = {
  link: string
  label: string
  icon?: string
  method?: string
}

export type CustomerWithDepartments = {
  name: string
  url: string
  active: boolean
  disabled: boolean
  subItems: Array<{ id: number; name: string; url: string; active: boolean }>
}

export interface HeaderItemsProps {
  baseName: string
  productName: string
  customerName: string
  headerMenuItems: HeaderMenuItem[]
  customersWithDepartments: CustomerWithDepartments[]
  authenticityToken: string
  texts: Texts
  user: User
}

/**
 * Renders an autouncle pro logo with text
 */
const HeaderItems = ({
  baseName,
  productName,
  customerName,
  headerMenuItems,
  customersWithDepartments,
  authenticityToken,
  texts,
  user,
}: HeaderItemsProps) => {
  /** Whenever the URL changes, this hook returns a new location object. */
  const location = useLocation()

  /** Get the value when select an option */
  const handleChange = (departmentItem) => {
    if (window.location.pathname === location.pathname) {
      // We are not in a SPA, follow the link directly
      window.location.href = departmentItem.value
    } else {
      // We are in a SPA, append the current pathname to the department root url
      window.location.href = departmentItem.value + `${location.pathname}`
    }
  }

  const handleMenuItemLinkChange = (item) => {
    if (item.method) {
      // Logs out the customer user
      post(item.value, {
        authenticity_token: authenticityToken,
        _method: item.method,
      })
    } else {
      // Opens Admin links in new pages
      window.open(item.value), "_blank"
    }
  }

  // Displays the selected department name if its active
  let selectedDepartmentName
  customersWithDepartments.forEach((c) => {
    const dep = c.subItems?.find((d) => d.active)
    if (dep) {
      selectedDepartmentName = dep.name
    }
  })

  // Returns a new array of customers with departments.
  const newCustomersWithDepartments = customersWithDepartments?.map((item) => {
    return {
      label: item.name,
      value: item.url,
      selected: item.active,
      disabled: item.disabled,
      subItems: item.subItems?.map((option) => {
        return {
          label: option.name,
          value: option.url,
          selected: option.active,
          disabled: false,
        }
      }),
    }
  })

  // Returns the selected menu item label
  const selectedMenuItemLink = headerMenuItems?.find((a) => {
    a.link === baseName
  })?.label

  // Returns a new array of items, including: Admin links for admins & Logout link for customers.
  const newHeaderMenuItems: Item[] = headerMenuItems?.map((item) => {
    return {
      label: item.label,
      value: item.link,
      active: item.label === selectedMenuItemLink,
      disabled: false,
      icon: item.icon,
      method: item.method,
    }
  })

  // Returns users initials or first letter of email address
  const userInitials =
    user?.firstName?.charAt(0).toUpperCase() +
      user?.lastName?.charAt(0).toUpperCase() ||
    user?.email.charAt(0).toUpperCase()

  return (
    <HeaderItemsWrapper width="full">
      <ProductName>{productName}</ProductName>
      <DropdownsWrapper>
        <DropdownSelection>
          {customersWithDepartments?.length === 1 &&
          customersWithDepartments[0].subItems?.length === 0 ? (
            <CustomerName>{customerName}</CustomerName>
          ) : (
            <DropdownButton
              menu={
                <MenuWindow
                  items={newCustomersWithDepartments}
                  onChange={handleChange}
                  selected={baseName}
                />
              }
            >
              <DropdownButtonLabel>
                <Text fontColor="blue_800" fontWeight="600" textType="body">
                  {selectedDepartmentName || customerName}
                </Text>
                <Text
                  fontColor="gray_600"
                  fontWeight="500"
                  textType="smallText"
                >
                  {selectedDepartmentName
                    ? texts.entityLabels.department
                    : texts.entityLabels.customer}
                </Text>
              </DropdownButtonLabel>
            </DropdownButton>
          )}
        </DropdownSelection>

        <DropdownSelection>
          {headerMenuItems && (
            <DropdownButton
              menu={
                <MenuWindow
                  items={newHeaderMenuItems}
                  onChange={handleMenuItemLinkChange}
                />
              }
            >
              <DropdownButtonLabel>
                <UserLabel>{userInitials}</UserLabel>
              </DropdownButtonLabel>
            </DropdownButton>
          )}
        </DropdownSelection>
      </DropdownsWrapper>
    </HeaderItemsWrapper>
  )
}

HeaderItems.defaultProps = {
  customersWithDepartments: [],
}

export default HeaderItems
